import React, { useEffect, useState } from "react";
import { Loader2, RefreshCw, CheckCircle2, AlertTriangle } from "lucide-react";
import { adminApi } from "../../lib/api";
import { Button } from "../ui/button";
import { useToast } from "../../hooks/use-toast";

// Confirming Discovery Calls against Calendly, rather than trusting what the
// booking page managed to tell us.
export const CalendlyBookingsPanel = ({ onSynced }) => {
  const { toast } = useToast();
  const [s, setS] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    adminApi.calendly.reporting().then(setReport).catch(() => setReport(null));
    return adminApi.calendly.status().then(setS).catch(() => setS({ token: false }));
  };
  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setBusy(true);
    try {
      const r = await adminApi.calendly.sync(30);
      toast({ title: r.ok ? `${r.booked} confirmed, ${r.cancelled} cancelled` : "Couldn't reach Calendly" });
      await load();
      if (onSynced) onSynced();
    } finally {
      setBusy(false);
    }
  };

  const connect = async () => {
    setBusy(true);
    try {
      const r = await adminApi.calendly.createWebhook(window.location.origin);
      toast({ title: r.webhook?.state === "active" ? "Live updates connected" : "Webhook created" });
      await load();
    } catch (e) {
      toast({ title: e?.response?.data?.detail || "Calendly wouldn't accept that", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const savePosition = async (pos) => {
    await adminApi.calendly.settings(Number(pos));
    load();
  };

  if (!s) return null;

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6" data-testid="calendly-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-display font-bold text-navy flex items-center gap-2">
            Discovery Calls, confirmed by Calendly
            {s.token
              ? <CheckCircle2 size={15} className="text-teal" />
              : <AlertTriangle size={15} className="text-orange" />}
          </h3>
          <p className="text-xs text-slate-500 mt-1 leading-relaxed max-w-2xl">
            {s.token
              ? "We read your Calendly bookings every hour and confirm them against the answers given on the site, so a booking is only counted once it genuinely exists. Cancellations are picked up the same way. Calls booked outside the website are recorded here in full, but are not sent to GA4."
              : "No Calendly token on the server, so bookings can only be recorded as intents."}
          </p>
        </div>
        {s.token && (
          <div className="flex items-center gap-2">
            {!s.webhook && (
              <Button onClick={connect} disabled={busy} variant="outline" className="rounded-full h-10 text-xs border-teal/40 text-teal hover:bg-teal/10" data-testid="calendly-connect-webhook">
                Turn on live updates
              </Button>
            )}
            <Button onClick={refresh} disabled={busy} variant="outline" className="rounded-full h-10 text-xs" data-testid="calendly-sync">
              {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <RefreshCw size={14} className="mr-1.5" />}
              Refresh bookings now
            </Button>
          </div>
        )}
      </div>

      {s.token && (
        <div className="grid md:grid-cols-2 gap-4 mt-5">
          <div className="rounded-xl border border-slate-100 bg-slate-50/70 p-4">
            <p className="text-xs font-bold text-navy">Our question's position in Calendly</p>
            <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
              Calendly matches prefilled answers by position, so this has to be the place "How did you hear about us?"
              sits in your Discovery Call questions.
            </p>
            <select
              value={s.prefill_position}
              onChange={(e) => savePosition(e.target.value)}
              data-testid="calendly-position"
              className="mt-3 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange/40"
            >
              {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>Question {n}</option>)}
            </select>
            {s.questions?.length > 0 && (
              <div className="mt-3">
                <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">What Calendly currently asks</p>
                <ol className="mt-1 space-y-1">
                  {s.questions.map((q) => (
                    <li key={q.position} className="text-[11px] text-slate-500 leading-snug">
                      {q.position}. {q.question}
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>

          <div className="rounded-xl border border-slate-100 bg-slate-50/70 p-4">
            <p className="text-xs font-bold text-navy">Last check</p>
            {s.last_sync ? (
              <p className="text-[11px] text-slate-500 mt-1 leading-relaxed" data-testid="calendly-last-sync">
                {String(s.last_sync.at).slice(0, 16).replace("T", " ")} — {s.last_sync.events} bookings read,
                {" "}{s.last_sync.booked} confirmed, {s.last_sync.cancelled} cancelled,
                {" "}{s.last_sync.external ?? s.last_sync.ignored ?? 0} booked outside the website.
              </p>
            ) : (
              <p className="text-[11px] text-slate-500 mt-1">Not checked yet.</p>
            )}
            <p className="text-[11px] text-slate-400 mt-3 leading-relaxed">
              {s.webhook
                ? "A live webhook is also connected, so bookings appear within seconds."
                : s.webhook_note || "Checked automatically every hour."}
            </p>
          </div>
        </div>
      )}

      {report && report.confirmed_calls > 0 && (
        <div className="mt-5 rounded-xl border border-slate-100 bg-slate-50/70 p-4" data-testid="calendly-reporting">
          <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
            <p className="text-xs font-bold text-navy">What GA4 was told</p>
            <p className="text-[11px] text-slate-500" data-testid="calendly-reporting-counts">
              <strong className="text-navy">{report.confirmed_calls}</strong> confirmed call(s) ·
              {" "}<strong className="text-navy">{report.reported_to_ga4}</strong> sent to GA4
              {report.reported_to_ga4 > 0 && (
                <> ({report.reported_by_browser} from the browser, {report.reported_by_server} from the server)</>
              )}
              {report.external_calls > 0 && <> · {report.external_calls} booked outside the website</>}
              {report.not_consented > 0 && <> · {report.not_consented} without analytics consent</>}
            </p>
          </div>
          <p className="text-[11px] text-slate-400 mt-2 leading-relaxed">{report.note}</p>

          {report.needs_reconciliation.length > 0 && (
            <div className="mt-3 rounded-lg bg-amber-50 p-3" data-testid="calendly-reconcile">
              <p className="text-[11px] font-bold text-amber-900">
                {report.needs_reconciliation.length} booking(s) to check for a duplicate in GA4
              </p>
              {report.needs_reconciliation.slice(0, 5).map((r) => (
                <p key={r.id} className="text-[11px] text-amber-900 mt-1 leading-snug">
                  {String(r.when).slice(0, 16).replace("T", " ")} — {r.note}
                </p>
              ))}
            </div>
          )}

          <ul className="mt-3 space-y-1">
            {report.recent.map((r) => (
              <li key={r.id} className="text-[11px] text-slate-500 flex flex-wrap gap-x-2 leading-snug"
                  data-testid={`calendly-booking-${r.id}`}>
                <span className="text-slate-400">{String(r.when).slice(0, 16).replace("T", " ")}</span>
                <span>{r.source || (r.origin === "external" ? "Booked outside the website" : "Website booking")}</span>
                <span className={r.route === "not_reported" ? "text-slate-400" : "text-teal"}>
                  · {r.route_label}
                </span>
                {r.reason && <span className="text-slate-400">· {r.reason}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CalendlyBookingsPanel;
